import { Component, computed, inject, input, model, output, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { I18nService } from '../../../services/i18n.service';
import { NotificationService } from '../../../services/notification.service';
import { PushPermissionStatus } from '../../../stores/notification.store';
import { PushNotificationToggleComponent } from '../push-notification-toggle/push-notification-toggle';
import { EmailToggleComponent } from '../email-toggle/email-toggle';
import { NzModalComponent, NzModalModule } from 'ng-zorro-antd/modal';
import { AssetNavButtonComponent } from '../../../components/asset-nav-button/asset-nav-button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { SpinnerComponent } from '../../../components/spinner/spinner';
@Component({
  selector: 'app-all-notifications-settings',
  templateUrl: './all-notifications-settings.html',
  styleUrls: ['./all-notifications-settings.scss'],
  imports: [
    PushNotificationToggleComponent,
    EmailToggleComponent,
    NzModalComponent,
    NzModalModule,
    AssetNavButtonComponent,
    SpinnerComponent,
    NzIconModule,
  ],
})
export class AllNotificationsSettingsComponent {
  public route = inject(ActivatedRoute);
  public i18n = inject(I18nService);
  private notification = inject(NotificationService);
  public settings = model<any>();
  public loading = input<boolean>(false);
  public success = input<boolean>(false);

  public updateSettings = output<any>();
  public showAllNotification = signal<boolean>(false);
  public permission_error = signal<boolean>(false);
  public requestingNotificationPermission = signal<boolean>(false);
  public notificationPermissionStatus = signal<PushPermissionStatus>(
    this.notification.PermissionStatus
  );
  public pushNotificationModalVisible = signal<boolean>(false);

  public resetTrigger = signal<boolean>(false);
  public customErrorMessage = signal<string | undefined>(undefined);
  public customAlertTitle = signal<string | undefined>(undefined);
  public customAlertMessage = signal<string | undefined>(undefined);
  public instructionsAddToHomeScreenIOS = signal<boolean>(false);

  private pushNotificationModalResolve?: (value: void | PromiseLike<void>) => void;
  private pushNotificationModalReject?: (reason?: unknown) => void;

  constructor() {
    this.route.url.pipe().subscribe((url) => {
      this.showAllNotification.set(url.length === 2);
    });
  }

  public IsSafari = computed(() => /^((?!chrome|android).)*safari/i.test(navigator.userAgent));

  public UserEmail = computed(() => this.settings()?.email ?? '');

  public allNotificationsEmailSelected = computed(() => {
    const s = this.settings();
    if (!s) return false;
    const keys = this.getNotificationKeys('Email');
    return keys.length > 0 && keys.every((key) => s[key] === true);
  });

  public allNotificationsPushSelected = computed(() => {
    const s = this.settings();
    if (!s) return false;
    const keys = this.getNotificationKeys('Push');
    return keys.length > 0 && keys.every((key) => s[key] === true);
  });

  private getNotificationKeys(type: 'Email' | 'Push'): string[] {
    const s = this.settings();
    if (!s) return [];
    return Object.keys(s).filter(
      (key) => key.includes('notification') && key.includes(type) && key !== `notifications${type}`
    );
  }

  public toggleAllEmail(value: boolean) {
    const currentSettings = { ...this.settings() };
    if (!currentSettings) return;

    currentSettings._disable_observation = true;
    const keys = this.getNotificationKeys('Email');
    keys.forEach((key) => (currentSettings[key] = value));
    currentSettings._disable_observation = false;

    this.updateSettings.emit(currentSettings);
    this.settings.set(currentSettings);
  }

  public toggleAllPush(value: boolean) {
    if (!this.settings()) return;

    this.triggerNotificationPermissionPrompt().then(
      () => {
        const currentSettings = { ...this.settings() };
        currentSettings._disable_observation = true;
        const keys = this.getNotificationKeys('Push');
        keys.forEach((key) => (currentSettings[key] = value));
        currentSettings._disable_observation = false;

        this.updateSettings.emit(currentSettings);
        this.settings.set(currentSettings);
      },
      () => {
        // Reset the toggle UI if permission was denied

        this.updateSettings.emit(this.settings());
        this.resetTrigger.update((v) => !v);
      }
    );
  }

  public requestNotificationPermission() {
    this.permission_error.set(false);
    this.requestingNotificationPermission.set(true);

    this.notification.subscribeToNotifications(
      (status: PushPermissionStatus) => {
        this.requestingNotificationPermission.set(false);
        if (status !== PushPermissionStatus.granted) {
          this.notificationPermissionStatus.set(status);

          if (status === PushPermissionStatus.denied) {
            this.permission_error.set(true);
          }

          this.closePushNotificationModal(false);
          return;
        }

        this.notificationPermissionStatus.set(PushPermissionStatus.granted);
        this.closePushNotificationModal(true);
      },
      (_reason) => {
        this.permission_error.set(true);
        this.requestingNotificationPermission.set(false);
        this.notificationPermissionStatus.set(PushPermissionStatus.denied);
        this.closePushNotificationModal(false);
      }
    );
  }

  private triggerNotificationPermissionPrompt(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        if (!('Notification' in window)) {
          if (this.IsSafari()) {
            this.customAlert(
              this.i18n.langString('notifications_not_supported_title'),
              this.i18n.langString('safari_not_supporting_notifications'),
              true
            );
          } else {
            this.customAlert(
              this.i18n.langString('notifications_not_supported_title'),
              this.i18n.langString('browser_not_supporting_notifications')
            );
          }
          return reject();
        }

        if (Notification.permission !== 'granted') {
          this.displayNotificationPermissionPrompt(resolve, reject);
        } else {
          resolve();
        }
      } catch (error: any) {
        this.customError(this.i18n.langString('error_triggering_notification_prompt') + error.message);
        reject();
      }
    });
  }

  private displayNotificationPermissionPrompt(resolve: any, reject: any) {
    this.pushNotificationModalResolve = resolve;
    this.pushNotificationModalReject = reject;
    this.pushNotificationModalVisible.set(true);
  }

  public closePushNotificationModal(resolve: boolean) {
    if (resolve) {
      this.pushNotificationModalResolve?.();
    } else {
      this.pushNotificationModalReject?.();
    }
    this.pushNotificationModalVisible.set(false);
    this.requestingNotificationPermission.set(false);
    this.pushNotificationModalResolve = undefined;
    this.pushNotificationModalReject = undefined;
  }

  public customError(message: string) {
    this.customErrorMessage.set(message);
  }

  public customAlert(title: string, message: string, instructionsLink = false) {
    this.customAlertTitle.set(title);
    this.customAlertMessage.set(message);
    this.instructionsAddToHomeScreenIOS.set(instructionsLink);
  }

  public closeCustomAlert() {
    this.customAlertMessage.set(undefined);
    this.instructionsAddToHomeScreenIOS.set(false);
  }
}
